import React, { PureComponent } from 'react';
import { createSelector } from 'reselect';
import { csvParse, utcParse, bisector, scaleOrdinal } from 'd3'
import Selector from './Selector';
import PeriodSelector from './PeriodSelector';
import BenchmarkSelector from './BenchmarkSelector';
import Timeline from './Timeline';
import Footnote from './Footnote';
import { PerformanceValues } from './MarketValues';
import { DATE, MARKET, NAV, VOLUME, G10K_NAV, G10K_MARKET } from './constants';
import { translate } from './translate';
import { timelineColors } from './pallette';
import { periods } from './periods';
import './TimelineWidget.css';

const colorScale = scaleOrdinal()
  .range(timelineColors)

const benchmarkFields = createSelector(
  (props, state) => state.data,
  (props, state) => state.selectedBenchmarks,
  (data, selectedBenchmarks) => {
    const data0 = data[0]
    return selectedBenchmarks
      .map(symbol => Object.keys(data0).find(col => data0[col] === symbol))
      .filter(col => !!col)
      .map(col => 'price_level_' + col.charAt(col.length - 1))
  }
)
const filteredData = createSelector(
  benchmarkFields,
  (props, state) => state.data,
  (props, state) => state.selectedPeriod,
  (props, state) => state.selectedDisplay === 'G10K'
    ? state.selectedMetric === 'NAV' ? G10K_NAV : G10K_MARKET
    : state.selectedMetric === 'NAV' ? NAV : MARKET,
  (props, state) => state.selectedDisplay,
  (props, state) => state.data[state.data.length - 1][DATE],
  (props, state) => state.customPeriod,
  (benchmarkFields, data, selectedPeriod, selectedMetric, selectedDisplay, latestDate, customPeriod) => {
    const timeRange = selectedPeriod === 'CUSTOM' ? customPeriod : [periods[selectedPeriod](latestDate), latestDate]
    const filteredData = data.filter(d => d[DATE] >= timeRange[0] && d[DATE] <= timeRange[1])
    const fields = [selectedMetric].concat(benchmarkFields) // ['price', 'price_level_A', 'price_level_B']

    if (selectedDisplay === 'PRICE' && benchmarkFields.length === 0) {
      return filteredData.map(d => ({
        ...d,
        yValues: fields.map(field => d[field])
      }))
    }
    else if (selectedDisplay === 'PRICE') {
      const yValues0 = fields.map(
        field => filteredData.find(d => d[field] !== null)[field]
      )
      return filteredData.map(d => ({
        ...d,
        yValues: fields.map(
          (field, i) => d[field] === null ? null : (d[field] - yValues0[i]) / yValues0[i]
        )
      }))
    }
    else if (selectedDisplay === 'G10K') {
      const yValues0 = fields.map(
        field => filteredData.find(d => d[field] !== null)[field]
      )
      return filteredData.map(d => ({
        ...d,
        yValues: fields.map(
          (field, i) => d[field] === null ? null : 10000 * (1 + (d[field] - yValues0[i]) / yValues0[i])
        )
      }))
    }
  }
)

const availableTimeRange = createSelector(
  (props, state) => state.data[0][DATE],
  (props, state) => state.data[state.data.length - 1][DATE],
  (firstDate, latestDate) => [firstDate, latestDate]
)

const dataAtMouse = createSelector(
  filteredData,
  (props, state) => state.mouseDate,
  (data, mouseDate) => {
    if (!mouseDate) return data[data.length - 1]
    const bisectDate = bisector((d, x) => d[DATE] - x).left
    const index = bisectDate(data, mouseDate)
    return data[index]
  }
)

const lookupTranslate = createSelector(
  props => props.i18n,
  props => props.lang,
  (i18n, lang) => id => (
    { id, label: translate(id, i18n, lang) }
  )
)

const parseDate = utcParse('%-m/%-d/%Y')
const parseCsvNum = value => value === '' ? null : +value

class TimelineWidget extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      selectedDisplay: 'G10K',
      selectedMetric: 'NAV',
      selectedPeriod: 'YTD',
      customPeriod: null,
      selectedBenchmarks: [],
      mouseDate: null,
      data: csvParse(window[this.props.fundMarketDataVarName], (d, i, cols) => cols.reduce(
        function (obj, colName) {
          if (colName.indexOf('price_level') > -1) {
            obj[colName] = parseCsvNum(obj[colName])
          }
          return obj
        }, {
          ...d,
          [DATE]:        parseDate(d[DATE]), 
          [MARKET]:      parseCsvNum(d[MARKET]), 
          [NAV]:         parseCsvNum(d[NAV]), 
          [G10K_NAV]:    parseCsvNum(d[G10K_NAV]), 
          [G10K_MARKET]: parseCsvNum(d[G10K_MARKET]), 
          [VOLUME]:     [parseCsvNum(d[VOLUME])]
        }
      )),
      benchmarks: window[this.props.fundBenchmarksVarName] || []
    }
    this.handleMouseDate = this.handleMouseDate.bind(this)
  }
  handleSelectDisplay(selectedDisplay) {
    this.setState({selectedDisplay})
  }
  handleSelectMetric(selectedMetric) {
    this.setState({selectedMetric})
  }
  handleSelectPeriod(selectedPeriod, customPeriod) {
    this.setState({selectedPeriod, customPeriod})
  }
  handleSelectBenchmarks(selectedBenchmarks) {
    this.setState({selectedBenchmarks})
  }
  handleMouseDate(mouseDate) {
    this.setState({mouseDate})
  }
  render() {
    const {
      fundTicker, fundName, width, i18n, lang, 
      hideVolumeGraph, hideMarketOption, hideMarketVsNavSelector, hideG10kFootnote, g10kFootnoteTextId,
    } = this.props
    const { benchmarks, mouseDate, customPeriod,
            selectedMetric, selectedDisplay, selectedPeriod, selectedBenchmarks } = this.state
    const data = filteredData(this.props, this.state)
    const marginLeft = 30
    const marginRight = width >= 678 ? 100 : 70

    // Ensure mapping of benchmark to color based on their position in benchmarks array rather than their order of selection
    // This way, the same benchmark always has the same color
    const benchmarkIndices = selectedBenchmarks.map(sym => benchmarks.indexOf(benchmarks.find(d => d.symbol === sym)))
    colorScale
      .range([timelineColors[0], ...benchmarkIndices.map(index => timelineColors[index + 1])])

    const benchmarksSelector = <BenchmarkSelector
      onSelect={this.handleSelectBenchmarks.bind(this)}
      options={benchmarks}
      selected={selectedBenchmarks}
      i18n={i18n}
      lang={lang}
    />

    return (
      <div>
        <div className='Selector-row'>
          <Selector
            optionClassName='Selector-option-as-link'
            onSelect={this.handleSelectDisplay.bind(this)}
            options={
              ['G10K', !hideMarketVsNavSelector ? '' : '']
                .map(lookupTranslate(this.props))
                .map(option => option.id === 'G10K' ? {
                  ...option, 
                  label: () => (
                    <div>
                      {option.label}
                      {hideG10kFootnote ? null : <Footnote mark='i'>
                        <div>{translate(g10kFootnoteTextId, i18n, lang)}</div>
                      </Footnote>}
                    </div> )
                } : option)
            }
            selected={selectedDisplay}
          />
        </div>
        <div className='Selector-row'>
          {hideMarketVsNavSelector ? null : <Selector
            onSelect={this.handleSelectMetric.bind(this)}
            options={['NAV', 'MARKET'].filter(d => hideMarketOption && d === 'MARKET' ? false : true).map(lookupTranslate(this.props))}
            selected={selectedMetric}
          />}
          { hideMarketVsNavSelector === false && benchmarks.length > 0 ? benchmarksSelector : null
          }
        </div>
        <div className='Selector-row'>
          { hideMarketVsNavSelector !== false && benchmarks.length > 0 ? benchmarksSelector : null}
          <PeriodSelector
            availableTimeRange={availableTimeRange(this.props, this.state)}
            onSelect={this.handleSelectPeriod.bind(this)}
            selected={selectedPeriod}
            customPeriod={customPeriod}
            i18n={i18n}
            lang={lang}
          />
        </div>
        <div>
          <div className='TimelineWidget-legend'>{
            [{symbol: fundTicker, name:fundName}]
              .concat(selectedBenchmarks
                .map(selectedSymbol => benchmarks.find(({symbol}) => selectedSymbol === symbol))
              )
              .map(({symbol, name}, i, a) => (
                <div
                  className='TimelineWidget-legend-item'
                  key={i}
                  style={{color:colorScale(i)}}
                >
                  <span dangerouslySetInnerHTML={{
                    __html: name /* we set html like this because name can contain html tags or entities (like TM) */
                  }} />
                  <span> ({symbol})</span>
                </div>
              ))
          }</div>
          <PerformanceValues
            shouldLabelAsOf={!mouseDate}
            valueFormat={selectedDisplay === 'G10K' ? ',.0f' : selectedBenchmarks.length > 0 ? '.2%' : ',.2f'}
            datum={dataAtMouse(this.props, this.state)} 
            i18n={i18n}
            lang={lang}
            hideVolume={hideVolumeGraph}
          />
        </div>
        { width !== null ? [
          <Timeline
            key='price-graph'
            data={data}
            yDimension={'yValues'}
            yFormat={selectedDisplay === 'G10K' ? ',.0f' : selectedBenchmarks.length > 0 ? '.2%' : ',.2f'}
            width={width + marginLeft}
            height={262}
            colorScale={colorScale}
            mouseDate={mouseDate}
            onMouseDate={this.handleMouseDate}
            margin={{ t:10, r:marginRight, b: hideVolumeGraph ? 25 : 5, l:marginLeft }} 
            displayBottomTickValues={hideVolumeGraph}
          />,
          hideVolumeGraph ? null : <Timeline
            key='volume-graph'
            title={<div>
              <span style={{verticalAlign:'top'}}>{fundTicker} Daily Volume</span>
              {<Footnote mark='i'><div>{translate('INFO_VOL', i18n, lang)}</div></Footnote>}
            </div>}
            data={data}
            yDimension={VOLUME}
            yFormat={'.3s'}
            width={width + marginLeft}
            height={120}
            colorScale={colorScale}
            margin={{ t:15, r:marginRight, b:30, l:marginLeft }} 
            mouseDate={mouseDate}
            onMouseDate={this.handleMouseDate} 
            yStartFromZero={true}
            renderAsBars={true}
          />
        ] : null }
      </div>
    )
  }
}

TimelineWidget.defaultProps = {
  fundMarketDataVarName: 'fundMarketData',
  fundBenchmarksVarName: 'fundBenchmarks',
}

export default TimelineWidget


// WEBPACK FOOTER //
// ./src/TimelineWidget.js